import { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api.js";
import type { AppUser } from "./types.js";

/** Include every authorization boundary, not just the login's numeric id. */
export function accountScope(user: AppUser | null): string {
  return JSON.stringify(user
    ? [user.id, user.tenant_id, user.domain_id, user.role, user.sip_endpoint_id]
    : null);
}

/** Whether a failed query is asked again: twice, but never a cancelled request or a refusal. */
export function retryQuery(failureCount: number, error: Error): boolean {
  return error.name === "AbortError" ||
    (error instanceof ApiError && (error.status === 401 || error.status === 403))
    ? false
    : failureCount < 2;
}

/** Never reuse this client for another session, even if the same person signs back in. */
export function createSessionQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        retry: retryQuery,
        refetchOnWindowFocus: true,
      },
    },
  });
}

export function retireSessionQueryClient(client: QueryClient): void {
  // Cancellation is synchronous internally; do not yield before erasing both query and
  // mutation caches. Detached callbacks retain only this retired client, never the next one.
  void client.cancelQueries();
  client.clear();
}
